import * as i0 from '@angular/core';
import { Directive, HostListener, NgModule } from '@angular/core';
import { convertNonEnglishDigits, formatIban, currencyFormat } from '@digipay/strings';

/**
 * Iranian IBAN numbers start with IR followed by some numbers and dashes
 */
let FormDirectiveIban = /*#__PURE__*/(() => {
  class FormDirectiveIban {
    constructor(elementRef) {
      this.elementRef = elementRef;
      const el = this.elementRef.nativeElement;
      el.setAttribute('type', 'tel');
      el.setAttribute('pattern', '[0-9]*');
    }
    keydown(event) {
      const key = convertNonEnglishDigits(event.key);
      if (isNaN(parseInt(key, 10)) && key !== 'Backspace' && !event.ctrlKey) {
        event.preventDefault();
      }
      if (event.key === 'e') {
        event.preventDefault();
      }
    }
    focusIn(event) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      this.elementRef.nativeElement.focus();
      this.elementRef.nativeElement.setSelectionRange(999999, 999999);
    }
    change(event) {
      const originalVal = event.target.value;
      let val = originalVal;
      if (event.inputType === 'deleteContentBackward') {
        // backspace
        if (val === 'IR-') {
          val = '';
        }
      }
      val = formatIban(val);
      if (originalVal !== val) {
        this.elementRef.nativeElement.value = val;
        this.elementRef.nativeElement.dispatchEvent(new Event('input'));
      }
    }
    static {
      this.ɵfac = function FormDirectiveIban_Factory(t) {
        return new (t || FormDirectiveIban)(i0.ɵɵdirectiveInject(i0.ElementRef));
      };
    }
    static {
      this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
        type: FormDirectiveIban,
        selectors: [["", "iban", ""]],
        hostBindings: function FormDirectiveIban_HostBindings(rf, ctx) {
          if (rf & 1) {
            i0.ɵɵlistener("keydown", function FormDirectiveIban_keydown_HostBindingHandler($event) {
              return ctx.keydown($event);
            })("focusin", function FormDirectiveIban_focusin_HostBindingHandler($event) {
              return ctx.focusIn($event);
            })("input", function FormDirectiveIban_input_HostBindingHandler($event) {
              return ctx.change($event);
            });
          }
        }
      });
    }
  }
  return FormDirectiveIban;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * Displays a keypad keyboard on mobile phones for
 * having a better UX
 */
let FormDirectiveNumPad = /*#__PURE__*/(() => {
  class FormDirectiveNumPad {
    constructor(elementRef) {
      this.elementRef = elementRef;
      this.attributeValue = '';
      const el = this.elementRef.nativeElement;
      if (el.getAttribute('numpad')) {
        this.attributeValue = el.getAttribute('numpad');
      }
      el.setAttribute('type', 'tel');
      el.setAttribute('pattern', '[0-9]*');
    }
    keydown(event) {
      if (event.key === 'e') {
        event.preventDefault();
      }
    }
    inputKeyUp(event) {
      const originalVal = event.target.value;
      let val = convertNonEnglishDigits(event.target.value);
      val = val.replace(/[^\d]/g, '');
      switch (this.attributeValue) {
        case 'card-number':
          if (event.key === 'Backspace' && originalVal[originalVal.length - 1] === '-') {
            // help user to easily remove the last `-` character
            val = originalVal.substr(0, originalVal.length - 1);
          } else {
            if (val.length <= 18) {
              // separate numbers by dash
              val = val.replace(/(\d{4})/ig, '$1-');
              if (val[val.length - 1] === '-') {
                val = val.substr(0, val.length - 1);
              }
            }
          }
          break;
        case 'price':
          val = currencyFormat(val);
          break;
        default:
          break;
      }
      if (originalVal !== val) {
        this.elementRef.nativeElement.value = val;
        this.elementRef.nativeElement.dispatchEvent(new Event('input'));
      }
    }
    static {
      this.ɵfac = function FormDirectiveNumPad_Factory(t) {
        return new (t || FormDirectiveNumPad)(i0.ɵɵdirectiveInject(i0.ElementRef));
      };
    }
    static {
      this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
        type: FormDirectiveNumPad,
        selectors: [["", "numpad", ""]],
        hostBindings: function FormDirectiveNumPad_HostBindings(rf, ctx) {
          if (rf & 1) {
            i0.ɵɵlistener("keydown", function FormDirectiveNumPad_keydown_HostBindingHandler($event) {
              return ctx.keydown($event);
            })("keyup", function FormDirectiveNumPad_keyup_HostBindingHandler($event) {
              return ctx.inputKeyUp($event);
            });
          }
        }
      });
    }
  }
  return FormDirectiveNumPad;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * This directive replaces the entered value of the input
 * with circle without having to use "type=password"
 */
let FormDirectiveSecretInput = /*#__PURE__*/(() => {
  class FormDirectiveSecretInput {
    constructor(elementRef) {
      this.elementRef = elementRef;
      const el = this.elementRef.nativeElement;
      el.setAttribute('type', 'tel');
      el.setAttribute('pattern', '[0-9]*');
    }
    keydown(event) {
      if (event.key === 'e') {
        event.preventDefault();
      }
    }
    inputKeyUp(event) {
      let val = convertNonEnglishDigits(event.target.value);
      val = val.replace(/[\d]/g, '⬤');
      event.target.value = val;
    }
    static {
      this.ɵfac = function FormDirectiveSecretInput_Factory(t) {
        return new (t || FormDirectiveSecretInput)(i0.ɵɵdirectiveInject(i0.ElementRef));
      };
    }
    static {
      this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
        type: FormDirectiveSecretInput,
        selectors: [["", "secretInput", ""]],
        hostBindings: function FormDirectiveSecretInput_HostBindings(rf, ctx) {
          if (rf & 1) {
            i0.ɵɵlistener("keydown", function FormDirectiveSecretInput_keydown_HostBindingHandler($event) {
              return ctx.keydown($event);
            })("keyup", function FormDirectiveSecretInput_keyup_HostBindingHandler($event) {
              return ctx.inputKeyUp($event);
            });
          }
        }
      });
    }
  }
  return FormDirectiveSecretInput;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let FormDirectiveCellNumber = /*#__PURE__*/(() => {
  class FormDirectiveCellNumber {
    constructor(elementRef) {
      this.elementRef = elementRef;
      const el = this.elementRef.nativeElement;
      el.setAttribute('type', 'tel');
      el.setAttribute('pattern', '[0-9]*');
      this.setEventListeners(el);
    }
    setEventListeners(element) {
      element.addEventListener('keypress', this.keyDownHandler);
      element.addEventListener('keydown', this.keyDownHandler);
      element.addEventListener('keyup', this.keyUpHandler);
    }
    keyUpHandler(event) {
      if (event.key === 'Backspace') {
        return;
      }
      let val = event.target.value;
      val = convertNonEnglishDigits(val);
      if (val.length > 11) {
        val = val.substr(0, 11);
      }
      // replace everything except numbers
      val = val.replace(/[^\d]/g, '');
      event.target.value = val;
    }
    keyDownHandler(event) {
      if (event.key === 'e') {
        event.preventDefault();
      }
      if (event.target.value.length === 11 && event.key !== 'Backspace') {
        event.preventDefault();
      }
    }
    onInputChange(event) {
      this.elementRef.nativeElement.value = convertNonEnglishDigits(event.target.value);
    }
    static {
      this.ɵfac = function FormDirectiveCellNumber_Factory(t) {
        return new (t || FormDirectiveCellNumber)(i0.ɵɵdirectiveInject(i0.ElementRef));
      };
    }
    static {
      this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
        type: FormDirectiveCellNumber,
        selectors: [["", "cellNumber", ""]],
        hostBindings: function FormDirectiveCellNumber_HostBindings(rf, ctx) {
          if (rf & 1) {
            i0.ɵɵlistener("input", function FormDirectiveCellNumber_input_HostBindingHandler($event) {
              return ctx.onInputChange($event);
            });
          }
        }
      });
    }
  }
  return FormDirectiveCellNumber;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * Default "maxlength" attribute of the inputs is not reliable
 */
let FormDirectiveMaxLength = /*#__PURE__*/(() => {
  class FormDirectiveMaxLength {
    constructor(elementRef) {
      this.elementRef = elementRef;
      const el = this.elementRef.nativeElement;
      this.maxLength = parseInt(el.getAttribute('realMaxLength'), 10);
      this.listen(el);
    }
    preventEntering(e) {
      if (e.key === 'Backspace' || e.code === 8) {
        return;
      }
      if (['Tab', 'ArrowLeft', 'ArrowRight'].indexOf(e.key) >= 0) {
        return;
      }
      if (e.target.value.length >= this.maxLength) {
        e.preventDefault();
      }
    }
    listen(element) {
      element.addEventListener('keydown', this.preventEntering.bind(this));
      element.addEventListener('keypress', this.preventEntering.bind(this));
      element.addEventListener('keyup', e => {
        const val = e.target.value;
        if (val.length > this.maxLength) {
          e.target.value = val.substr(0, this.maxLength);
        }
      });
    }
    static {
      this.ɵfac = function FormDirectiveMaxLength_Factory(t) {
        return new (t || FormDirectiveMaxLength)(i0.ɵɵdirectiveInject(i0.ElementRef));
      };
    }
    static {
      this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
        type: FormDirectiveMaxLength,
        selectors: [["", "realMaxLength", ""]]
      });
    }
  }
  return FormDirectiveMaxLength;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let FormDirectiveNotPastable = /*#__PURE__*/(() => {
  class FormDirectiveNotPastable {
    constructor() {}
    keydown(event) {
      event.preventDefault();
    }
    static {
      this.ɵfac = function FormDirectiveNotPastable_Factory(t) {
        return new (t || FormDirectiveNotPastable)();
      };
    }
    static {
      this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
        type: FormDirectiveNotPastable,
        selectors: [["", "notPastable", ""]],
        hostBindings: function FormDirectiveNotPastable_HostBindings(rf, ctx) {
          if (rf & 1) {
            i0.ɵɵlistener("paste", function FormDirectiveNotPastable_paste_HostBindingHandler($event) {
              return ctx.keydown($event);
            });
          }
        }
      });
    }
  }
  return FormDirectiveNotPastable;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let FormDirectivesModule = /*#__PURE__*/(() => {
  class FormDirectivesModule {
    static {
      this.ɵfac = function FormDirectivesModule_Factory(t) {
        return new (t || FormDirectivesModule)();
      };
    }
    static {
      this.ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
        type: FormDirectivesModule
      });
    }
    static {
      this.ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({});
    }
  }
  return FormDirectivesModule;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/*
 * Public API Surface of form-directives
 */

/**
 * Generated bundle index. Do not edit.
 */

export { FormDirectiveCellNumber, FormDirectiveIban, FormDirectiveMaxLength, FormDirectiveNotPastable, FormDirectiveNumPad, FormDirectiveSecretInput, FormDirectivesModule };
